import React, { useState, useMemo, useCallback } from 'react';
import { FaBars, FaTimes } from 'react-icons/fa';

import { Book, Upload, Users, LogOut, ChartBar, History, UserRoundCheck, Image, File } from 'lucide-react';

import TopMenuMobile from './TopMobile';
import NavigationMenu from './NavigationMenu';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ThemeSwitcher from '../components/ThemeSwitcher';
import { textColor, borderColor, pageGutter } from '../styles/styles';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeProvider';
import lhrc_logo from '../assets/logo/lhrc_logo.svg';
import lhrc_logo_light from '../assets/logo/lhrc_logo_light.svg';
const SearchForm = React.memo(({ tr }) => (
    <form className={clsx('hidden lg:flex items-center max-w-sm mx-auto')}>
        <div className="relative w-full">
            <div className="absolute inset-y-0 start-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
                </svg>
            </div>
            <input
                type="text"
                className={clsx(
                    `
                bg-gray-50 dark:bg-gray-700/50 
                pl-10
                border border-gray-300 dark:border-gray-600
                text-gray-900 dark:text-gray-100
                text-sm rounded-lg p-2.5 w-full
                focus:outline-none focus:ring-0 focus:border-[#41A6D7] 
                 `,
                    textColor
                )}
                placeholder={tr('search')}
                aria-label={tr('search')}
            />
        </div>
    </form>
));

const AuthButtons = React.memo(({ tr }) => (
    <div className="hidden lg:flex space-x-2 justify-center">
        <LanguageSwitcher />
        <ThemeSwitcher />
        <div className="flex items-center justify-center space-x-2 sm:space-x-4 flex-shrink-0">
            <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    window.location.href = '/login';
                }}
                className="p-1.5 h-10 w-10 bg-transparent border border-gray-400 hover:border-gray-500 cursor-pointer rounded-lg transition-all duration-200 hover:shadow-sm active:scale-95"
                aria-label={`Logout`}
                title="Logout"
            >
                <LogOut className="w-5 h-5 stroke-1 text-gray-900 dark:text-yellow-500" />
            </motion.button>
        </div>
    </div>
));

const ProtectedHeader = () => {
    const { t } = useTranslation();
    const tr = useCallback((key) => t(`header:${key}`), [t]);

    const { theme } = useTheme();
    const logo = theme === 'dark' ? lhrc_logo_light : lhrc_logo;

    const menuItems = useMemo(
        () => [
            { to: '/dashboard', label: tr('dashboard'), icon: ChartBar },
            { to: '/timeline', label: tr('timeline'), icon: History },
            { to: '/constitutions', label: tr('constitution'), icon: Book },
            { to: '/upload', label: tr('upload'), icon: Upload },
            { to: '/media', label: tr('media'), icon: Image },
            { to: '/material', label: tr('material'), icon: File },
            { to: '/roles', label: tr('roles'), icon: UserRoundCheck },
            { to: '/users', label: tr('users'), icon: Users },
            
        ],
        [tr]
    );

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const toggleMobileMenu = useCallback(() => {
        setIsMobileMenuOpen((prev) => !prev);
    }, []);

    return (
        <header className="sticky w-full top-0 z-50">
            <nav className={clsx('bg-white dark:bg-gray-800', borderColor, 'border-b')}>
                <div className={clsx('flex flex-wrap justify-between', 'max-w-screen-3xl mx-auto', pageGutter, 'items-center py-2 lg:py-3')}>
                    <a href="#/dashboard" className="flex items-center space-x-3 rtl:space-x-reverse">
                        <img src={logo} alt="LHRC Logo" className="w-[70px] sm:w-[85px] md:w-[90px] lg:w-[100px] h-auto mb-0" />
                        <div className={clsx('text-[#50A1CA] dark:text-white font-bold text-4xl  mb-1')}>Katiba</div>
                    </a>
                    <div className="lg:hidden">
                        <button onClick={toggleMobileMenu} className="text-gray-600" aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={isMobileMenuOpen}>
                            {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
                        </button>
                    </div>
                    <div className="flex items-center space-x-2">
                        <SearchForm tr={tr} />
                        <AuthButtons tr={tr} />
                    </div>
                </div>
            </nav>
            <nav className={clsx('hidden lg:flex border-t border-b mb-2', 'bg-white dark:bg-gray-800', borderColor)}>
                <div className={clsx('w-full max-w-screen-3xl mx-auto lg:block py-2', pageGutter)}>
                    <NavigationMenu items={menuItems} />
                </div>
            </nav>
            {isMobileMenuOpen && (
                <nav className="lg:hidden bg-transparent shadow-lg shadow-gray-300/40 fixed top-14 left-0 w-full z-50">
                    <div className="max-w-screen-xl py-3">
                        <TopMenuMobile items={menuItems} toggleMobileMenu={toggleMobileMenu} />
                    </div>
                </nav>
            )}
        </header>
    );
};

export default React.memo(ProtectedHeader);
